import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Stack,
  Chip,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { format } from "date-fns";
import DownloadMenuButton from "./DownloadMenuButton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../../services/api";

export default function ClientProfileView() {
  const { uid } = useParams(); // uid here = linkedClients document ID
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showBankModal, setShowBankModal] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [bankAuthLink, setBankAuthLink] = useState("");
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 7);
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load linkedClients doc directly
        const clientDoc = await getDoc(doc(db, "linkedClients", uid));
        if (clientDoc.exists()) {
          setClient({ id: clientDoc.id, ...clientDoc.data() });
        } else {
          console.warn("Linked client not found for ID:", uid);
          setClient(null);
        }

        // Then load receipts by clientUid
        if (clientDoc.exists()) {
          const clientUid = clientDoc.data().clientUid;
          const receiptsSnap = await getDocs(
            query(collection(db, "receipts"), where("uid", "==", clientUid))
          );

          const sorted = receiptsSnap.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          setReceipts(sorted);
        }
      } catch (err) {
        console.error("Failed to load client profile:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [uid]);

  // --- Bank Modal Logic ---
  const openBankModal = async () => {
    setShowBankModal(true);
    if (banks.length === 0) {
      try {
        const res = await api.get("/bank/institutions?country=IE");
        setBanks(res.data);
      } catch (err) {
        alert("Failed to load banks");
      }
    }
  };

  const handleBankConnect = async () => {
    if (!selectedBank) return;
    setConnecting(true);
    try {
      const res = await api.post("/bank/connect", {
        clientUid: client.clientUid,
        institutionId: selectedBank,
        redirectTo: `/client-profile/${uid}`,
      });
      setShowBankModal(false);
      setSelectedBank("");
      setConnecting(false);
      if (res.data?.link) {
        setBankAuthLink(res.data.link);
        setShowShareLinkModal(true);
      } else {
        alert("Failed to start bank connection.");
      }
    } catch (err) {
      setConnecting(false);
      alert("Failed to start bank connection.");
    }
  };

  // ---

  const handleRemoveClient = async () => {
    if (!client) return;
    const confirmed = window.confirm(
      `Are you sure you want to remove ${client.clientEmail}?`
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "linkedClients", client.id));
      navigate("/accountant");
    } catch (err) {
      console.error("Failed to remove client:", err);
    }
  };

  const filteredReceipts = receipts.filter((r) => {
    if (!selectedMonth) return true;
    const rawDate = r.uploadedAt?.toDate?.() ?? new Date(r.uploadedAt);
    if (isNaN(rawDate.getTime())) return false;
    return rawDate.toISOString().slice(0, 7) === selectedMonth;
  });

  const availableMonths = [
    ...new Set(
      receipts
        .map((r) => {
          const d = r.uploadedAt?.toDate?.() ?? new Date(r.uploadedAt);
          return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 7);
        })
        .filter(Boolean)
    ),
  ];

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`Receipt Report for ${client.clientEmail}`, 14, 22);

    const rows = filteredReceipts.map((r) => {
      const rawDate = r.uploadedAt?.toDate?.() ?? new Date(r.uploadedAt);
      const formattedDate = isNaN(rawDate.getTime())
        ? "—"
        : format(rawDate, "dd MMM yyyy");

      return [
        r.fileName || "—",
        formattedDate,
        r.amount ? `€${r.amount.toFixed(2)}` : "—",
      ];
    });

    autoTable(doc, {
      startY: 30,
      head: [["File Name", "Date", "Amount"]],
      body: rows,
    });

    doc.save(`Receipts-${client.clientEmail}.pdf`);
    setDownloadSuccess(true);
  };

  const handleDownloadSingleReceipt = async (receipt) => {
    try {
      const res = await api.get(`/receipts/${receipt.id}/download`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = receipt.fileName || "receipt.jpg";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Failed to download receipt:", err);
      alert("Download failed. Please try again.");
    }
  };

  const getCategoryDisplay = (category) => {
    switch ((category || "").toLowerCase()) {
      case "meals":
        return { label: "Meals", icon: "🥗", color: "primary" };
      case "travel":
        return { label: "Travel", icon: "✈️", color: "secondary" };
      case "office":
        return { label: "Office", icon: "🖇️", color: "info" };
      case "supplies":
        return { label: "Supplies", icon: "📦", color: "success" };
      case "software":
        return { label: "Software", icon: "💻", color: "warning" };
      default:
        return {
          label: category || "Uncategorized",
          icon: "📁",
          color: "default",
        };
    }
  };

  const monthlyTotals = filteredReceipts.reduce(
    (acc, r) => {
      if (typeof r.amount === "number") acc.total += r.amount;
      if (typeof r.vat === "number") acc.vat += r.vat;
      acc.count += 1;
      return acc;
    },
    { total: 0, vat: 0, count: 0 }
  );

  const handleDownloadZip = async () => {
    try {
      const receiptIds = filteredReceipts.map((r) => r.id);
      if (receiptIds.length === 0) {
        alert("No receipts to download.");
        return;
      }

      const res = await api.post(
        "/receipts/zip",
        { receiptIds },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Receipts-${client.clientEmail || "client"}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadSuccess(true);
    } catch (err) {
      console.error("❌ Failed to download ZIP file:", err);
      alert("Failed to download ZIP file.");
    }
  };

  const categoryData = Object.values(
    filteredReceipts.reduce((acc, r) => {
      const category = r.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = { name: category, value: 0 };
      }
      acc[category].value += typeof r.amount === "number" ? r.amount : 0;
      return acc;
    }, {})
  );

  const COLORS = [
    "#1976d2",
    "#9c27b0",
    "#2e7d32",
    "#ff9800",
    "#e91e63",
    "#607d8b",
  ];

  const visibleReceipts = filteredReceipts.filter((r) =>
    r.fileName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ----- UI Render -----
  if (loading) {
    return (
      <Box p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box p={4}>
        <Typography variant="h6">Client not found.</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* --- Bank Selection Modal --- */}
      <Dialog open={showBankModal} onClose={() => setShowBankModal(false)}>
        <DialogTitle>Connect Bank Account</DialogTitle>
        <DialogContent>
          <Typography>Select the client's bank:</Typography>
          <Select
            fullWidth
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            sx={{ mt: 2, minWidth: 220 }}
          >
            {banks.map((bank) => (
              <MenuItem value={bank.id} key={bank.id}>
                {bank.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBankModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBankConnect}
            disabled={!selectedBank || connecting}
          >
            {connecting ? "Connecting..." : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* --- End Bank Modal --- */}

      {/* --- Share Bank Link Modal --- */}
      <Dialog
        open={showShareLinkModal}
        onClose={() => setShowShareLinkModal(false)}
      >
        <DialogTitle>Share Link with Client</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Send this link to your client. <br />
            <b>
              They must log in and complete authentication to connect their bank
              account.
            </b>
            <br />
            (Required by banking regulations.)
          </Typography>
          <TextField
            fullWidth
            value={bankAuthLink}
            InputProps={{ readOnly: true }}
            sx={{ my: 2 }}
          />
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(bankAuthLink);
              }}
            >
              Copy Link
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                window.open(
                  `mailto:${client.clientEmail}?subject=Please complete your bank connection&body=Hi, please click the following link to securely connect your bank account: ${bankAuthLink}`,
                  "_blank"
                );
              }}
            >
              Email to Client
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShareLinkModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* --- End Share Link Modal --- */}

      <Box p={4}>
        <Typography variant="h4" gutterBottom>
          Client Profile
        </Typography>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6">{client.clientEmail}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Invited At: {client.invitedAt?.toDate?.()?.toDateString?.() || "—"}
          </Typography>
          <Typography variant="body2">
            Accepted At:{" "}
            {client.acceptedAt?.toDate?.()?.toDateString?.() || "—"}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            sx={{ mt: 2 }}
            onClick={handleRemoveClient}
          >
            Remove Client
          </Button>
        </Paper>

        <Paper
          sx={{
            p: 3,
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Bank Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {client.bankConnection?.connected
                ? `Connected to ${
                    client.bankConnection.institutionName ||
                    client.bankConnection.institutionId
                  } (since ${new Date(
                    client.bankConnection.linkedAt
                  ).toLocaleString()})`
                : "No bank account connected or pending client action."}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={openBankModal}
            sx={{ textTransform: "none" }}
          >
            Connect Bank
          </Button>
        </Paper>

        <Divider sx={{ mb: 4 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={1}
        >
          <Typography variant="h6">
            Receipts ({filteredReceipts.length}){" "}
            {selectedMonth
              ? `– ${new Date(`${selectedMonth}-01`).toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}`
              : "– All Months"}
          </Typography>

          <DownloadMenuButton
            onPdf={handleDownloadPdf}
            onZip={handleDownloadZip}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            availableMonths={availableMonths}
          />
        </Stack>

        {/* ✅ Summary below header, before table */}
        {filteredReceipts.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              px: 3,
              py: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              justifyContent: "flex-start",
              bgcolor: "#f9fafb",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>
                Total Receipts
              </Typography>
              <Typography variant="body1">{monthlyTotals.count}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>
                Total Amount
              </Typography>
              <Typography variant="body1">
                €{monthlyTotals.total.toFixed(2)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>
                Total VAT
              </Typography>
              <Typography variant="body1">
                €{monthlyTotals.vat.toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        )}

        {categoryData.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              bgcolor: "#f9fafb",
            }}
          >
            <Typography variant="subtitle2" mb={1} fontWeight={500}>
              Receipt Breakdown by Category
            </Typography>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={categoryData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {categoryData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {filteredReceipts.length === 0 ? (
          <Typography>No receipts uploaded for this month.</Typography>
        ) : (
          <Paper sx={{ overflowX: "auto" }}>
            <TextField
              label="Search Receipts"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleReceipts.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.fileName || "—"}</TableCell>
                    <TableCell>
                      {r.uploadedAt?.toDate?.()
                        ? format(r.uploadedAt.toDate(), "dd MMM yyyy")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {r.amount ? `€${r.amount.toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const { label, icon, color } = getCategoryDisplay(
                          r.category
                        );
                        return (
                          <Chip
                            label={`${icon} ${label}`}
                            color={color}
                            size="small"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadSingleReceipt(r)}
                        title="Download Receipt"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1} direction="row">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadSingleReceipt(r)}
                          title="Download Receipt"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>

                        <Button
                          size="small"
                          variant="contained"
                          onClick={async () => {
                            try {
                              await api.post("/quickbooks/push", {
                                accountantUid: auth.currentUser?.uid,
                                receiptData: {
                                  supplier: r.supplier || "Unknown Supplier",
                                  date:
                                    r.uploadedAt
                                      ?.toDate?.()
                                      ?.toISOString()
                                      ?.split("T")[0] ||
                                    new Date().toISOString().split("T")[0],
                                  total: r.amount || 0,
                                },
                              });
                              alert("✅ Pushed to QuickBooks!");
                            } catch (err) {
                              console.error(err);
                              alert(
                                "❌ Push failed: " +
                                  (err.response?.data?.message || err.message)
                              );
                            }
                          }}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#1976d2",
                          }}
                        >
                          Push to QuickBooks
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
        <Snackbar
          open={downloadSuccess}
          autoHideDuration={4000}
          onClose={() => setDownloadSuccess(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setDownloadSuccess(false)}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            Download successful 🎉
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
